/**
 * Voice recording hook: encapsulates MediaRecorder, VAD (auto-stop on silence),
 * and the SSE voice-stream upload pipeline.
 *
 * Extracted from ChatSession.jsx (was ~250 lines of tightly coupled refs).
 *
 * Usage:
 *   const voice = useVoiceRecording({ sessionId, onUserMessage, onAssistantMessage, onVoiceStats });
 *   voice.start() / voice.stop() / voice.cancel()
 *   voice.recording / voice.processing / voice.recordSecs / voice.vadState / voice.silenceMs / voice.micStream / voice.aiSpeaking / voice.progressLabel
 *   voice.vadEnabled, voice.setVadEnabled
 *   voice.stopAiAudio()
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

const VAD_SPEECH_RMS = 0.04;
const VAD_SILENCE_RMS = 0.025;
const VAD_MIN_SPEECH_MS = 600;
const VAD_AUTO_STOP_SILENCE_MS = 2000;
const RECORD_HARD_CAP_SECS = 120;
const MIN_BLOB_BYTES = 2500;

export function useVoiceRecording({ sessionId, onUserMessage, onAssistantUpdate, onAssistantFinal, onVoiceStats }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [micStream, setMicStream] = useState(null);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [progressLabel, setProgressLabel] = useState("");
  const [vadEnabled, setVadEnabled] = useState(true);
  const [vadState, setVadState] = useState("idle");
  const [silenceMs, setSilenceMs] = useState(0);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);
  const vadRafRef = useRef(null);
  const vadAudioCtxRef = useRef(null);
  const vadAnalyserRef = useRef(null);
  const vadSourceRef = useRef(null);
  const vadSpokeAtRef = useRef(0);
  const vadLastVoiceAtRef = useRef(0);
  const vadAutoStoppedRef = useRef(false);

  const stopVAD = useCallback(() => {
    if (vadRafRef.current) { cancelAnimationFrame(vadRafRef.current); vadRafRef.current = null; }
    try { vadSourceRef.current?.disconnect?.(); } catch (e) { /* already disconnected */ }
    try { vadAnalyserRef.current?.disconnect?.(); } catch (e) { /* already disconnected */ }
    if (vadAudioCtxRef.current) {
      try { vadAudioCtxRef.current.close?.(); } catch (e) { /* already closed */ }
    }
    vadAudioCtxRef.current = null;
    vadAnalyserRef.current = null;
    vadSourceRef.current = null;
    vadSpokeAtRef.current = 0;
    vadLastVoiceAtRef.current = 0;
    setVadState("idle");
    setSilenceMs(0);
  }, []);

  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      try { mr.stop(); } catch (err) { console.warn("MediaRecorder stop failed", err); }
    } else {
      setRecording(false);
    }
  }, []);

  const stopAiAudio = useCallback(() => {
    if (audioPlayerRef.current) {
      try { audioPlayerRef.current.pause(); } catch (err) { console.warn("pause AI audio", err); }
      audioPlayerRef.current = null;
      setAiSpeaking(false);
    }
  }, []);

  const playAudio = useCallback((b64) => {
    try {
      if (audioPlayerRef.current) {
        try { audioPlayerRef.current.pause(); } catch (e) { /* noop */ }
      }
      const audio = new Audio("data:audio/mp3;base64," + b64);
      audioPlayerRef.current = audio;
      setAiSpeaking(true);
      audio.onended = () => { setAiSpeaking(false); audioPlayerRef.current = null; };
      audio.onerror = () => { setAiSpeaking(false); audioPlayerRef.current = null; };
      audio.play().catch(() => { setAiSpeaking(false); audioPlayerRef.current = null; });
    } catch (e) { setAiSpeaking(false); }
  }, []);

  const startVAD = useCallback((stream) => {
    if (!vadEnabled) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.4;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      vadAudioCtxRef.current = ctx;
      vadAnalyserRef.current = analyser;
      vadSourceRef.current = source;
      vadSpokeAtRef.current = 0;
      vadLastVoiceAtRef.current = 0;
      vadAutoStoppedRef.current = false;
      setVadState("listening");

      const buf = new Uint8Array(analyser.fftSize);
      const tick = () => {
        const an = vadAnalyserRef.current;
        if (!an) return;
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        const now = performance.now();

        if (rms >= VAD_SPEECH_RMS) {
          if (!vadSpokeAtRef.current) vadSpokeAtRef.current = now;
          vadLastVoiceAtRef.current = now;
          setVadState("speaking");
          setSilenceMs(0);
        } else if (rms < VAD_SILENCE_RMS && vadSpokeAtRef.current) {
          const spokenFor = now - vadSpokeAtRef.current;
          const silenceFor = vadLastVoiceAtRef.current ? now - vadLastVoiceAtRef.current : 0;
          if (spokenFor >= VAD_MIN_SPEECH_MS) {
            setVadState("silence");
            setSilenceMs(Math.min(silenceFor, VAD_AUTO_STOP_SILENCE_MS));
            if (silenceFor >= VAD_AUTO_STOP_SILENCE_MS && !vadAutoStoppedRef.current) {
              vadAutoStoppedRef.current = true;
              try { stopRecording(); } catch (err) { console.warn("VAD auto-stop failed", err); }
              return;
            }
          }
        }
        vadRafRef.current = requestAnimationFrame(tick);
      };
      vadRafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      console.warn("VAD startup failed", e);
    }
  }, [vadEnabled, stopRecording]);

  const start = useCallback(async () => {
    if (recording || processing) return;
    stopAiAudio();
    cancelledRef.current = false;
    setRecordSecs(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1, sampleRate: 16000 },
      });
      streamRef.current = stream;
      setMicStream(stream);
      const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const mimeType = mimeCandidates.find(m => MediaRecorder.isTypeSupported(m)) || "";
      const mrOpts = { audioBitsPerSecond: 24000 };
      if (mimeType) mrOpts.mimeType = mimeType;
      const mr = new MediaRecorder(stream, mrOpts);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stopVAD();
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
        setMicStream(null);
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        setRecording(false);

        if (cancelledRef.current) { setRecordSecs(0); return; }

        const usedMime = mr.mimeType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type: usedMime });
        if (blob.size < MIN_BLOB_BYTES) {
          toast.error("Aufnahme zu kurz", { description: "Bitte mindestens 1 Sekunde sprechen." });
          setRecordSecs(0);
          return;
        }
        setProcessing(true);
        setProgressLabel("transcribe");
        const ext = usedMime.includes("mp4") ? "mp4" : usedMime.includes("ogg") ? "ogg" : "webm";
        const recordedSecs = Math.max(1, Math.floor(recordSecs)) || 1;
        const form = new FormData();
        form.append("audio", blob, `voice.${ext}`);
        form.append("duration_seconds", String(recordedSecs));
        let assistantTmpId = null;
        try {
          const token = localStorage.getItem("novatus_token");
          const resp = await fetch(`${API}/training/sessions/${sessionId}/voice-stream`, {
            method: "POST",
            body: form,
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
          const reader = resp.body.getReader();
          const decoder = new TextDecoder("utf-8");
          let buffer = "";
          let finalAssistantMsg = null;
          let finalVoiceStats = null;
          let userMsgPushed = false;
          let firstChunkSeen = false;

          const handleEvent = (ev, dataStr) => {
            let payload = null;
            try { payload = dataStr ? JSON.parse(dataStr) : null; } catch { payload = null; }
            if (ev === "transcript") {
              setProgressLabel("thinking");
            } else if (ev === "user_message" && payload) {
              onUserMessage?.(payload);
              if (payload.voice_stats) onVoiceStats?.(payload.voice_stats);
              userMsgPushed = true;
            } else if (ev === "chunk" && payload) {
              if (!firstChunkSeen) {
                firstChunkSeen = true;
                setProcessing(false);
                setProgressLabel("");
                assistantTmpId = "stream-" + Date.now();
                onAssistantUpdate?.({ tmpId: assistantTmpId, initial: true, delta: payload.delta || "" });
              } else {
                onAssistantUpdate?.({ tmpId: assistantTmpId, initial: false, delta: payload.delta || "" });
              }
            } else if (ev === "done" && payload) {
              finalAssistantMsg = payload.assistant_message;
              finalVoiceStats = payload.voice_stats;
            } else if (ev === "error" && payload) {
              throw new Error(payload.detail || "Sprach-Antwort fehlgeschlagen");
            }
          };

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let idx;
            while ((idx = buffer.indexOf("\n\n")) !== -1) {
              const raw = buffer.slice(0, idx);
              buffer = buffer.slice(idx + 2);
              let ev = "message";
              const dataLines = [];
              for (const line of raw.split("\n")) {
                if (line.startsWith("event:")) ev = line.slice(6).trim();
                else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
              }
              handleEvent(ev, dataLines.join("\n"));
            }
          }

          if (finalAssistantMsg) {
            onAssistantFinal?.({ tmpId: assistantTmpId, message: finalAssistantMsg });
          }
          if (finalVoiceStats) onVoiceStats?.(finalVoiceStats);
          setProcessing(false);
          setProgressLabel("");

          if (!userMsgPushed) return;

          if (finalAssistantMsg?.content) {
            setAiSpeaking(true);
            api.post("/tts", { text: finalAssistantMsg.content })
              .then(({ data: tts }) => {
                if (tts?.audio_base64) playAudio(tts.audio_base64);
                else setAiSpeaking(false);
              })
              .catch(() => setAiSpeaking(false));
          }
        } catch (err) {
          if (assistantTmpId) onAssistantFinal?.({ tmpId: assistantTmpId, message: null });
          toast.error(err?.message || "Sprach-Antwort fehlgeschlagen");
          setProcessing(false);
          setProgressLabel("");
        } finally {
          setRecordSecs(0);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      startVAD(stream);
      const startedAt = Date.now();
      timerRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - startedAt) / 1000);
        setRecordSecs(secs);
        if (secs >= RECORD_HARD_CAP_SECS) stopRecording();
      }, 250);
    } catch (e) {
      toast.error("Mikrofon-Zugriff verweigert", { description: "Bitte erlaube den Mikrofon-Zugriff in den Browser-Einstellungen." });
    }
  }, [recording, processing, sessionId, startVAD, stopRecording, stopVAD, stopAiAudio, playAudio, onUserMessage, onAssistantUpdate, onAssistantFinal, onVoiceStats, recordSecs]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    stopRecording();
  }, [stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== "inactive") {
        try { mr.stop(); } catch (err) { console.warn("unmount: stop recorder", err); }
      }
      stopVAD();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const playMessageAudio = useCallback(async (text) => {
    try {
      const { data } = await api.post("/tts", { text });
      if (data.audio_base64) playAudio(data.audio_base64);
    } catch { toast.error("Audio-Wiedergabe fehlgeschlagen"); }
  }, [playAudio]);

  return {
    recording, processing, recordSecs, micStream, aiSpeaking, progressLabel,
    vadEnabled, setVadEnabled, vadState, silenceMs,
    start, stop: stopRecording, cancel,
    stopAiAudio, playAudio, playMessageAudio,
  };
}
